import React, { useEffect, useState } from 'react';
import DefaultCard from '../../../../components/default/DefaultCard';
import DefaultToolBar from '../../../../components/default/DefaultToolBar';
import LazyLoading from '../../../../components/LazyLoading';
import SelectTwo, { SelectTwoMapperOptions } from '../../../../components/SelectTwo';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import usePackingOrderService from '../../../../app/services/hooks/usePackingOrderService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { PackingOrderRequiredStockGroup, PackingOrderRequirementGroup } from '../../../../app/models/PackingOrder';
import { PackingOrderOutputProductForm } from '../../../../app/models/PackingOrder';

interface ProductOption {
  id: number | string;
  name: string;
  category_id?: number | null;
  quality_id?: number | null;
  unit_id?: number | null;
}

interface Props {
  packingOrderId: number;
  requiredStock: PackingOrderRequiredStockGroup[];
  outputProducts: PackingOrderOutputProductForm[];
  products: ProductOption[];
  status?: string;
  onReload: () => void;
}

interface AddingState {
  outputProductId: number | null;
  groupKey: string | null;
  productId: number | string | null;
  quantity: string;
}

const defaultAdding: AddingState = {
  outputProductId: null,
  groupKey: null,
  productId: null,
  quantity: '',
};

const fmtQty = (value: any) => {
  if (value == null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return n % 1 === 0 ? n.toLocaleString('es-CL') : n.toLocaleString('es-CL', { maximumFractionDigits: 2 });
};

const PackingOrderInputProductSection = ({
  packingOrderId,
  requiredStock,
  outputProducts,
  products,
  status,
  onReload,
}: Props) => {
  const locked = status !== undefined && status !== 'CREATED';
  const {
    storeInputProduct,
    deleteInputProduct,
    fetchingInputProducts,
  } = usePackingOrderService();

  const [adding, setAdding] = useState<AddingState>(defaultAdding);
  const [saving, setSaving] = useState(false);

  const hasData = requiredStock?.length > 0;

  const getGroupLabel = (group: PackingOrderRequirementGroup) => {
    const parts: string[] = [];
    if (group.category_name) parts.push(group.category_name);
    if (group.quality_name) parts.push(group.quality_name);
    if (group.unit_name) parts.push(group.unit_name);
    return parts.join(' / ') || group.group_key;
  };

  const handleAddClick = (outputProductId: number, groupKey: string) => {
    setAdding({ outputProductId, groupKey, productId: null, quantity: '' });
  };

  const handleCancelAdd = () => {
    setAdding(defaultAdding);
  };

  const handleSaveAdd = (group: PackingOrderRequirementGroup) => {
    if (!adding.productId) {
      toast.warning('Seleccione un producto.');
      return;
    }
    const qty = parseFloat(adding.quantity);
    if (isNaN(qty) || qty <= 0) {
      toast.warning('Ingrese una cantidad válida mayor a cero.');
      return;
    }

    // Validar que no exceda el requerimiento
    const currentTotal = Number(group.specified_total ?? 0);
    const required = Number(group.required_quantity_per_unit);
    const remaining = required - currentTotal;

    if (qty > remaining) {
      toast.error(
        `La cantidad ingresada (${fmtQty(qty)}) excede el saldo pendiente (${fmtQty(remaining)}). ` +
        `Debe especificar exactamente ${fmtQty(required)} en total.`
      );
      return;
    }

    setSaving(true);
    storeInputProduct(
      {
        packing_order_id: packingOrderId,
        output_product_id: adding.outputProductId!,
        product_id: adding.productId,
        quantity: qty,
      } as any,
      {
        onSuccess: (response: ServiceResponse) => {
          setSaving(false);
          // Mantener el formulario abierto en el mismo grupo para agregar más
          setAdding((prev) => ({ ...prev, productId: null, quantity: '' }));
          toast.success('Insumo agregado.', { autoClose: 1500 });
          onReload();
        },
        onError: (response: ServiceResponse) => {
          setSaving(false);
          toast.error(response.message);
        },
        onFieldError: (response: ServiceResponse) => {
          setSaving(false);
          toast.error('Verifique los campos ingresados.');
        },
      }
    );
  };

  const handleDelete = (inputId: number) => {
    useReactConfirmAlert().requestConfirmation({
      title: '¿Eliminar insumo?',
      message: 'Se eliminará esta línea de especificación.',
      buttons: {
        confirmButton: {
          label: 'Sí, eliminar',
          onClick: () => {
            deleteInputProduct(inputId, {
              onSuccess: (response: ServiceResponse) => {
                toast.success('Insumo eliminado.', { autoClose: 1500 });
                onReload();
              },
              onError: (response: ServiceResponse) => {
                toast.error(response.message);
              },
            });
          },
        },
        cancelButton: {
          label: 'Cancelar',
          onClick: () => { /* cancelado */ },
        },
      },
    });
  };

  return (
    <DefaultCard>
      <DefaultToolBar
        left={
          <h5 className="mt-0 mb-0 card-title">
            Insumos requeridos
            {locked && (
              <span className="ms-2 badge bg-secondary" title="La orden ya no está en estado Creado. No se pueden modificar los insumos.">
                <i className="mdi mdi-lock" /> Bloqueado
              </span>
            )}
          </h5>
        }
      />
      <p className="text-muted small mb-3">
        {locked
          ? 'Los insumos están bloqueados porque la orden ya fue iniciada.'
          : 'Para cada grupo de requerimiento (categoría / calidad / unidad), especifique los productos a consumir por unidad de resultado. El sistema validará que la suma cubra lo requerido.'}
      </p>

      {!hasData ? (
        <p className="text-muted">
          Sin información de receta para los productos resultado de esta orden. Agregue productos
          resultado con receta definida.
        </p>
      ) : (
        requiredStock.map((group) => (
          <div key={`out-${group.output_product_id}`} className="mb-4">
            {/* Encabezado del producto resultado */}
            <div className="d-flex align-items-center gap-2 mb-2">
              <span className="fw-bold">{group.output_product_name}</span>
              <span className="badge bg-secondary">
                {fmtQty(group.output_quantity)} {group.output_unit_name ?? ''}
              </span>
            </div>

            {group.requirement_groups.length === 0 ? (
              <p className="text-muted small ms-2">Sin grupos de receta.</p>
            ) : (
              group.requirement_groups.map((reqGroup) => {
                const isAddingHere =
                  adding.outputProductId === group.output_product_id &&
                  adding.groupKey === reqGroup.group_key;

                const specifiedTotal = Number(reqGroup.specified_total ?? 0);
                const required = Number(reqGroup.required_quantity_per_unit);
                const isExact = specifiedTotal === required;
                const isOver = specifiedTotal > required;
                const isPartial = specifiedTotal > 0 && specifiedTotal < required;

                const borderClass = isExact
                  ? 'border-success'
                  : isOver
                  ? 'border-danger'
                  : isPartial
                  ? 'border-warning'
                  : '';

                const canAddMore = !locked && !isAddingHere && specifiedTotal < required;

                return (
                  <div
                    key={reqGroup.group_key}
                    className={`border rounded p-3 mb-3 ms-2 ${borderClass}`}
                  >
                    {/* Encabezado del grupo */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div>
                        <span className="fw-semibold">{getGroupLabel(reqGroup)}</span>
                        <span className="ms-2 text-muted small">
                          — Requiere: <strong>{fmtQty(required)}</strong> {reqGroup.unit_name ?? ''}
                          {' '}por unidad resultado
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {isExact ? (
                          <span className="badge bg-success">
                            <i className="mdi mdi-check" /> Exacto ({fmtQty(specifiedTotal)})
                          </span>
                        ) : isOver ? (
                          <span className="badge bg-danger">
                            <i className="mdi mdi-alert" /> Excedido ({fmtQty(specifiedTotal)} / {fmtQty(required)})
                          </span>
                        ) : isPartial ? (
                          <span className="badge bg-warning text-dark">
                            {fmtQty(specifiedTotal)} / {fmtQty(required)}
                          </span>
                        ) : (
                          <span className="badge bg-danger">Sin especificar</span>
                        )}
                        {canAddMore && (
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => handleAddClick(group.output_product_id, reqGroup.group_key)}
                            disabled={saving}
                          >
                            <i className="mdi mdi-plus" /> Agregar
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Productos especificados */}
                    {reqGroup.specified_products.length > 0 && (
                      <div className="table-responsive mb-2">
                        <table className="table table-sm table-bordered mb-0">
                              <thead className="table-light">
                            <tr>
                              <th>Producto</th>
                              <th className="text-end" style={{ width: 110 }}>Cant./unidad</th>
                              <th className="text-end" style={{ width: 110 }}>Total necesario</th>
                              <th className="text-end" style={{ width: 110 }}>Stock actual</th>
                              <th className="text-end" style={{ width: 100 }}>Saldo</th>
                              {!locked && <th style={{ width: 48 }}></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {reqGroup.specified_products.map((sp) => {
                              const qtyPerUnit = Number(sp.quantity);
                              const outputQty = Number(group.output_quantity);
                              const totalNeeded = qtyPerUnit * outputQty;
                              const stock = sp.current_stock != null ? Number(sp.current_stock) : null;
                              const saldo = stock != null ? stock - totalNeeded : null;
                              const stockInsufficient = stock != null && stock < totalNeeded;

                              return (
                                <tr key={sp.id}>
                                  <td>{sp.product_name}</td>
                                  <td className="text-end">
                                    {fmtQty(qtyPerUnit)} {sp.unit_name ?? ''}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {fmtQty(totalNeeded)} {sp.unit_name ?? ''}
                                  </td>
                                  <td className={`text-end ${stockInsufficient ? 'text-danger fw-semibold' : ''}`}>
                                    {stock != null ? (
                                      <>
                                        {fmtQty(stock)}
                                        {stockInsufficient && (
                                          <i
                                            className="mdi mdi-alert-circle ms-1"
                                            title={`Stock insuficiente: se necesitan ${fmtQty(totalNeeded)}`}
                                          />
                                        )}
                                      </>
                                    ) : '—'}
                                  </td>
                                  <td className={`text-end fw-semibold ${saldo != null && saldo < 0 ? 'text-danger' : saldo === 0 ? 'text-warning' : 'text-success'}`}>
                                    {saldo != null ? fmtQty(saldo) : '—'}
                                  </td>
                                  {!locked && (
                                    <td className="text-center">
                                      <button
                                        className="btn btn-sm btn-link text-danger p-0"
                                        title="Eliminar"
                                        onClick={() => handleDelete(sp.id)}
                                      >
                                        <i className="mdi mdi-delete" />
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Formulario de agregar */}
                    {!locked && isAddingHere && specifiedTotal < required && (
                      <div className="border-top pt-2 mt-1">
                        <p className="text-muted small mb-2">
                          Saldo pendiente:{' '}
                          <strong className="text-primary">
                            {fmtQty(required - specifiedTotal)} {reqGroup.unit_name ?? ''}
                          </strong>
                        </p>
                        <div className="row g-2 align-items-end">
                          <div className="col-md-6">
                            <label className="form-label small mb-1">Producto</label>
                            <SelectTwo
                              name="product_id"
                              inputValue={adding.productId}
                              options={SelectTwoMapperOptions(
                                products.filter((p) => {
                                  const catMatch = reqGroup.category_id == null || p.category_id === reqGroup.category_id;
                                  const qualMatch = reqGroup.quality_id == null || p.quality_id === reqGroup.quality_id;
                                  const unitMatch = reqGroup.unit_id == null || p.unit_id === reqGroup.unit_id;
                                  return catMatch && qualMatch && unitMatch;
                                }),
                                'id',
                                ['name']
                              )}
                              onChange={(opt: any) =>
                                setAdding((prev) => ({ ...prev, productId: opt?.value ?? null }))
                              }
                              placeholder="Buscar producto..."
                              isClearable
                            />
                          </div>
                          <div className="col-md-3">
                            <label className="form-label small mb-1">Cant. por unidad</label>
                            <input
                              type="number"
                              min={0.01}
                              step={0.01}
                              className="form-control form-control-sm"
                              value={adding.quantity}
                              onChange={(e) =>
                                setAdding((prev) => ({ ...prev, quantity: e.target.value }))
                              }
                              placeholder="0"
                            />
                          </div>
                          <div className="col-md-3 d-flex gap-1">
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleSaveAdd(reqGroup)}
                              disabled={saving}
                            >
                              {saving ? <span className="spinner-border spinner-border-sm" /> : <i className="mdi mdi-check" />}
                              {' '}Guardar
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={handleCancelAdd}
                              disabled={saving}
                            >
                              Cancelar
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        ))
      )}
    </DefaultCard>
  );
};

export default PackingOrderInputProductSection;
